import _ from 'lodash';

// Fields rendered on the "Contact Information For The Representative Completing This Application"
// page of the Recertification OCA. They use top-level Formik keys (e.g. `firstName`), but the
// same POC also has to be reflected in `payload.owners[]` so the backend can update the matching
// `owner_pg_credit_application` row by `ownerPgId`. Without this, the backend only updates the
// `credit_application` row from top-level fields, and the POC's owner_pg row keeps its old value
// (DR-10369).
const REPRESENTATIVE_CONTACT_FIELDS_TO_SYNC = [
  'firstName',
  'lastName',
  'title',
  'email',
  'phone',
  'mobilePhone',
];

const findPocOwnerIndexInOwners = (values: any): number => {
  if (!_.isArray(values.owners)) {
    return -1;
  }
  const pocIdxByIsOwnerPcFlag = _.findIndex(values.owners, (o: any) => !!_.get(o, 'isOwnerPc'));
  if (pocIdxByIsOwnerPcFlag >= 0) {
    return pocIdxByIsOwnerPcFlag;
  }
  const pocOwnerPgId = _.get(values, 'primaryContact.ownerPgId');
  if (!pocOwnerPgId) {
    return -1;
  }
  return _.findIndex(values.owners, (o: any) => _.get(o, 'ownerPgId') === pocOwnerPgId);
};

const applyRepresentativeContactFields = (values: any, ownerPath: string) => {
  _.forEach(REPRESENTATIVE_CONTACT_FIELDS_TO_SYNC, (fieldKey: string) => {
    const currentTopLevelValue = _.get(values, fieldKey);
    if (currentTopLevelValue === undefined) {
      return;
    }
    _.set(values, `${ownerPath}.${fieldKey}`, currentTopLevelValue);
  });
};

/**
 * Reflects the current values of the "Contact Information For The Representative" page into the
 * POC record that will be sent inside `payload.owners[]`, so `credit_application` and the POC's
 * `owner_pg_credit_application` row are updated consistently.
 *
 * Handles two topologies:
 *   1. POC lives inside `values.owners[]` (`isOwnerPc: true`, or its `ownerPgId` matches
 *      `primaryContact.ownerPgId`) - overwrite the fields of that entry in place.
 *   2. POC is a separate contact (`isOwnerPcOnly` scenario, absent from `values.owners[]`) - enrich
 *      `values.ownerPcOnly` with representative fields, `ownerPgId` and POC flags. `apiMapping`
 *      already appends `values.ownerPcOnly` to `payload.owners`, so the backend will resolve the
 *      correct owner_pg row via `ownerPgId`.
 *
 * Returns the mutated `values` (mutation is intentional for convenience). Other owners are not
 * touched.
 */
export const mergeRepresentativeContactEditsIntoOwners = (values: any): any => {
  if (!values) {
    return values;
  }

  const pocIdxInOwners = findPocOwnerIndexInOwners(values);
  if (pocIdxInOwners >= 0) {
    applyRepresentativeContactFields(values, `owners[${pocIdxInOwners}]`);
    return values;
  }

  const pocOwnerPgId = _.get(values, 'primaryContact.ownerPgId');
  if (!pocOwnerPgId) {
    return values;
  }

  if (!values.ownerPcOnly) {
    values.ownerPcOnly = {};
  }
  _.set(values, 'ownerPcOnly.ownerPgId', pocOwnerPgId);
  _.set(values, 'ownerPcOnly.isOwnerPc', true);
  _.set(values, 'ownerPcOnly.isOwnerPcOnly', true);
  applyRepresentativeContactFields(values, 'ownerPcOnly');

  return values;
};
